import React, { useEffect, useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { BUTTON_VARIANTS } from "@/src/components/button/Button";
import Modal from "@/src/components/modal/Modal";

import NotificationAPI from "../../../api/NotificationAPI";
import SwitchOnOff from "../../../components/common/tools/SwitchOnOff";
import ModuleLanguage from "../../../components/utils/ModuleLanguage";
import TranslateFormInput from "../../../components/utils/translateForm/TranslateFormInput";
import { getDefaultProjectLanguage } from "../../../utils/data/Content";

import type { ChangeEvent, JSX } from "react";

import type { ModalActions } from "@/src/components/modal/Modal";

import type {
  NotificationItem,
  NotificationRow,
  NotificationTranslation,
} from "../../../api/NotificationAPI.types";
import type { AppState } from "../../../reducers";

const moduleLanguageCustomProps = ["label"];

function ModalHeader(): JSX.Element {
  const { t } = useTranslation("editor-notifications");

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="shrink-0 grow basis-0 text-[1.171875rem] font-semibold">
        {t("categories")}
      </div>
    </div>
  );
}

interface NotificationTagModalProps {
  currentItem?: NotificationRow | null;
  nextRank?: number;
  open: boolean;
  onSubmit: () => void;
  onClose: () => void;
}

interface NotificationRouteParams {
  projectSlug: string;
}

function NotificationTagModal({
  currentItem,
  nextRank = 0,
  open,
  onSubmit,
  onClose,
}: NotificationTagModalProps): JSX.Element | null {
  const { t } = useTranslation("editor-notifications");
  const { project } = useSelector(
    (state: AppState) => state.architectureReducer,
  );
  const { languages } = useSelector((state: AppState) => state.fetchReducer);
  const { projectSlug } = useParams<NotificationRouteParams>();

  const [item, setItem] = useState<NotificationItem | null>(null);

  useEffect(() => {
    const {
      language_uuid: languageUuid,
      language_uuid_index: languageUuidIndex,
    } = getDefaultProjectLanguage(languages, project);

    if (!currentItem) {
      setItem({
        label: "",
        translations: [
          {
            default: true,
            language_uuid: languageUuid,
            tags: [],
            label: "",
          },
        ],
        translations_indexes: {
          language_uuid: languageUuidIndex,
          default: { true: [0] },
        },
      });
    } else {
      setItem(currentItem);
    }
  }, [currentItem, languages, open, project]);

  const { mutate: saveTag } = useMutation({
    mutationFn: async (tag: NotificationItem) => {
      if (currentItem) {
        return NotificationAPI.updateNotification({
          projectSlug,
          uuid: currentItem.uuid,
          notification: tag,
        });
      }

      return NotificationAPI.createNotification({
        projectSlug,
        payload: {
          push_notifications: [
            {
              label: tag.label,
              translations: tag.translations,
              default: !!tag.default,
              rank: nextRank,
            },
          ],
        },
      });
    },
    onSuccess: onSubmit,
    onError: () => {
      toast.error(t("genericError", { ns: "error" }));
    },
  });

  const handleSubmit = (): void => {
    if (!item) return;

    saveTag(item);
  };

  const onUpdateItem = (updatedItem: NotificationItem): void => {
    setItem({ ...updatedItem });
  };

  const onChangeTranslations = (
    translations: NotificationTranslation[],
  ): void => {
    setItem((prevState) =>
      prevState
        ? {
            ...prevState,
            translations,
          }
        : prevState,
    );
  };

  const onUpdateDefault = (value: boolean): void => {
    setItem((prevState) =>
      prevState
        ? {
            ...prevState,
            default: value,
          }
        : prevState,
    );
  };

  const onChangeLabel = (e: ChangeEvent<HTMLInputElement>): void => {
    const label = e.target.value.trim().toLowerCase();

    setItem((prevState) =>
      prevState
        ? {
            ...prevState,
            label,
          }
        : prevState,
    );
  };

  if (!item) return null;

  const modalActions: ModalActions[] = [
    {
      label: t("cancel"),
      buttonVariant: BUTTON_VARIANTS.SECONDARY,
      isFullWidth: true,
      isCloseAfterAction: true,
      onClick: onClose,
    },
    {
      label: t("confirm"),
      buttonVariant: BUTTON_VARIANTS.PRIMARY,
      isFullWidth: true,
      isCloseAfterAction: false,
      onClick: handleSubmit,
    },
  ];

  return (
    <Modal
      actions={modalActions}
      header={<ModalHeader />}
      isOpen={open}
      onClose={onClose}
      size="md"
    >
      <div className="flex flex-col">
        <div className="editor-form-section">
          <div className="editor-form-section-title">{t("tag")}</div>
          <div className="editor-form-input-box-h">
            <div className="editor-form-input" style={{ maxWidth: "300px" }}>
              <input
                maxLength={128}
                onChange={onChangeLabel}
                placeholder={t("tag_placeholder")}
                type="text"
                value={item.label}
              />
            </div>
          </div>
        </div>
        <div className="editor-form-section">
          <div className="editor-form-row editor-form-row-inline m-b-0">
            <div className="editor-form-input">
              <SwitchOnOff
                checked={item.default}
                iconName=""
                name="display_title"
                onChange={onUpdateDefault}
                size="sm"
              />
            </div>
            <div className="editor-form-label">{t("defaultNotif")}</div>
          </div>
        </div>
        <div className="editor-form-section">
          <div className="editor-form-section-title">
            {t("editor-content:languages")}
          </div>
          <ModuleLanguage
            isApiV4
            customProperties={moduleLanguageCustomProps}
            item={item}
            onAddLanguage={onUpdateItem}
            onUpdate={onUpdateItem}
          />
        </div>
        <div className="editor-form-section">
          <TranslateFormInput
            key={`notification-translate-input-${item.translations.length}`}
            fieldName="label"
            id="notification-translate-input-title"
            maxLength="128"
            title={t("translations")}
            translations={item.translations}
            translationsIndexes={item.translations_indexes}
            onChange={(translations: NotificationTranslation[]) => {
              onChangeTranslations(translations);
            }}
          />
        </div>
      </div>
    </Modal>
  );
}

export default NotificationTagModal;
